// WO-2026-09-14 Preprint productivity — the pure grouping / sorting / action rules behind the
// page, kept out of the component so a unit test can pin them (the way pitch-regions.ts pins the
// deck regions). Design: design repo docs/designs/2026-07-21-migration-daily-aaron-ted-angel/
// productivity-tab-v3.dc.html @ the 2026-09-14 re-import.
import {
  nextStates,
  preprintStatusLabel,
  type Action,
  type PreprintProductivityRow,
  type PreprintProductivityStage,
  type PreprintStatus,
  type RbacContext,
} from "@rrid/shared-types";

export interface BucketDef {
  key: string;
  title: string;
  tone: "blue" | "green";
  /** Membership AND sort order — the drawn order is the only ordering cue the design gives. */
  stages: readonly PreprintProductivityStage[];
}

/** The two drawn buckets, in the drawn order. A preprint in the first bucket also appears in the
 *  second — that duplication is drawn, so it is kept. To Pitch rows are never here: the pipeline
 *  starts at Selected, and only the editorial-gate rejects come back as "Editorial Rejected". */
export const PREPRINT_BUCKETS: readonly BucketDef[] = [
  { key: "active", title: "Active / In queue preprints", tone: "blue", stages: ["ACTIVE", "ON HOLD", "DORMANT"] },
  {
    key: "all",
    title: "All preprint statuses",
    tone: "green",
    stages: ["Editorial Rejected", "DISCARDED", "COMPLETE", "INVITED", "ACTIVE", "ON HOLD", "DORMANT"],
  },
];

/** Rows shown per bucket before "See all N preprints" (the export's default). */
export const PREVIEW_ROWS = 6;

/** Stage chip text — the app's labels win over the export's raw strings ("In Queue", not DORMANT;
 *  "Editorial Rejected", the Selected status, not the canvas's "Editorial Reject"). */
export const stageLabel = (stage: PreprintProductivityStage): string => preprintStatusLabel(stage);

/** Page-level search: title / AE / team / domain / stage — matched on the RENDERED stage label as
 *  well as the enum, so "in queue" finds DORMANT rows. */
export function matchesPage(row: PreprintProductivityRow, needle: string): boolean {
  if (!needle) return true;
  const hay = [
    row.title,
    row.assignedEditorName ?? "",
    row.assignedEditorEmail ?? "",
    row.team ?? "",
    row.domain ?? "",
    stageLabel(row.status),
    row.status,
  ]
    .join(" ")
    .toLowerCase();
  return hay.includes(needle);
}

/** Stage order (as listed for the bucket) → greenlits desc → days in stage desc → title. */
export function sortRows(
  rows: PreprintProductivityRow[],
  stages: readonly PreprintProductivityStage[],
): PreprintProductivityRow[] {
  return [...rows].sort(
    (a, b) =>
      stages.indexOf(a.status) - stages.indexOf(b.status) ||
      b.greenlitCount - a.greenlitCount ||
      (b.inStageDays ?? -1) - (a.inStageDays ?? -1) ||
      a.title.localeCompare(b.title),
  );
}

export interface Bucket {
  def: BucketDef;
  rows: PreprintProductivityRow[];
  proposed: number;
  unchecked: number;
  /** Preprints with a mini-team — what the Team/AE overview's MiniTeam column counts too. */
  miniTeam: number;
  greenlits: number;
}

/** Page search first, then each bucket's own title-only search; a bucket with nothing left is
 *  dropped (the export hides empty buckets rather than drawing an empty shell). */
export function bucketRows(
  rows: PreprintProductivityRow[],
  pageQuery: string,
  tableQueries: Record<string, string>,
): Bucket[] {
  const needle = pageQuery.trim().toLowerCase();
  const matched = rows.filter((r) => matchesPage(r, needle));
  return PREPRINT_BUCKETS.map((def) => {
    const tq = (tableQueries[def.key] ?? "").trim().toLowerCase();
    const inBucket = sortRows(
      matched.filter((r) => def.stages.includes(r.status)).filter((r) => !tq || r.title.toLowerCase().includes(tq)),
      def.stages,
    );
    return {
      def,
      rows: inBucket,
      proposed: inBucket.reduce((n, r) => n + r.proposedCount, 0),
      unchecked: inBucket.reduce((n, r) => n + r.uncheckedCount, 0),
      miniTeam: inBucket.filter((r) => r.miniTeamCount > 0).length,
      greenlits: inBucket.reduce((n, r) => n + r.greenlitCount, 0),
    };
  }).filter((b) => b.rows.length > 0);
}

/** The three choices the Change Status modal offers (the export's ACTIVE / DORMANT / ON HOLD). */
export const STATUS_CHOICES: readonly PreprintStatus[] = ["ACTIVE", "ON HOLD", "DORMANT"];

export interface RowActions {
  changeStatus: boolean;
  /** Which of STATUS_CHOICES the transition table allows from the row's current status. */
  statusTargets: PreprintStatus[];
  editorialReject: boolean;
  addReviewer: boolean;
  sendInvites: boolean;
}

const NO_ACTIONS: RowActions = {
  changeStatus: false,
  statusTargets: [],
  editorialReject: false,
  addReviewer: false,
  sendInvites: false,
};

/** Which card actions a viewer gets on a row. UI hiding only — the server re-checks every write:
 *  Change Status = `activatePreprint` on the guarded PATCH; Editorial Reject = `forceStatus`
 *  (→ DISCARDED + reason, terminal, so never on COMPLETE/DISCARDED); Add Reviewer = the AE+
 *  propose tell, and only where the Reviewer Proposals portal can show the preprint (its rail lists
 *  the three finding statuses); Send Invites = `sendJanewayInvites` (the existing CSV batch). A
 *  Selected-only row (Editorial Rejected) has nothing to act on. */
export function rowActions(
  row: PreprintProductivityRow,
  can: (action: Action, ctx?: RbacContext) => boolean,
): RowActions {
  if (!row.preprintId || row.status === "Editorial Rejected") return NO_ACTIONS;
  const ctx: RbacContext = { domain: row.domain };
  const next = nextStates("preprint", row.status);
  const statusTargets = STATUS_CHOICES.filter((s) => next.includes(s));
  const finding = row.status === "ACTIVE" || row.status === "ON HOLD" || row.status === "DORMANT";
  return {
    statusTargets,
    changeStatus: statusTargets.length > 0 && can("activatePreprint", ctx),
    editorialReject: next.includes("DISCARDED") && can("forceStatus", ctx),
    addReviewer: finding && can("findReviewersPreActivation", {}),
    sendInvites: row.status !== "COMPLETE" && row.status !== "DISCARDED" && can("sendJanewayInvites", ctx),
  };
}
